import { useState } from "react";
import { elapsed, fecha_actual } from "../hooks/elapsed";
import { Spinner } from "./Spinner";

export interface TramitesProps {
  data: any[];
  accion: (item: any, ejecutivoId?: string) => void;
  texto_accion: string;
  ejecutivos?: any[];
  cargando?: boolean;
}

export const TablaTramitesComponent = ({
  data,
  accion,
  texto_accion,
  ejecutivos = [],
  cargando = false,
}: TramitesProps) => {
  const [eje, setEje] = useState("");
  const [mostrarLeyenda, setMostrarLeyenda] = useState(false);

  return (
    <div className="w-full space-y-4">
      {/* --- Leyenda de Abreviaturas (Colapsable) --- */}
      <div className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <svg className="h-4 w-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            Glosario de Abreviaturas
          </div>
          <button
            onClick={() => setMostrarLeyenda(!mostrarLeyenda)}
            className="text-xs font-semibold text-brand-600 hover:text-brand-700"
          >
            {mostrarLeyenda ? "Ocultar" : "Mostrar"}
          </button>
        </div>

        {mostrarLeyenda && (
          <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-200/60 pt-4 text-xs text-slate-600 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            <div><strong className="text-slate-900">FS:</strong> Fecha Siniestro</div>
            <div><strong className="text-slate-900">FR:</strong> Fecha Registro</div>
            <div><strong className="text-slate-900">FE:</strong> Fecha Etapa</div>
            <div><strong className="text-slate-900">EA:</strong> Etapa Actual</div>
            <div><strong className="text-slate-900">SE:</strong> Sub Etapa</div>
            <div><strong className="text-slate-900">TR:</strong> Tiempo Registro</div>
            <div><strong className="text-slate-900">TUE:</strong> Tiempo Última Etapa</div>
            <div><strong className="text-slate-900">UM:</strong> Último Modificador</div>
            <div><strong className="text-slate-900">UA:</strong> Usuario Asignado</div>
          </div>
        )}
      </div>

      {/* --- Tabla Principal --- */}
      <div className="block w-full overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full border-collapse items-center bg-transparent text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500">
              <th className="p-4 px-6">Producto / Cobertura</th>
              <th className="p-4 px-6">Detalle Siniestro</th>
              <th className="p-4 px-6">Estatus y Etapa</th>
              <th className="p-4 px-6">Tiempos</th>
              <th className="p-4 px-6 text-right">Acción</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data && data.map((d: any) => (
              <tr
                key={`${d.t_id}-${d.s_id}-${d.t_fi_cobertura_id}`}
                className="transition-colors hover:bg-blue-50/20"
              >
                {/* Columna 1: Producto */}
                <td className="p-4 px-6 align-top">
                  <div className="flex flex-col space-y-1">
                    <span className="text-sm font-bold text-slate-900">
                      {d.t_fi_producto_id} - {d.s_fc_descripcion_producto}
                    </span>
                    <span className="text-xs font-medium text-brand-600">
                      {d.t_fi_cobertura_id} - {d.s_fc_descripcion_cobertura}
                    </span>
                    <span className="mt-1 text-xs text-slate-500">
                      Aseguradora: <strong className="text-slate-700">{d.s_fc_poliza_aseguradora}</strong>
                    </span>
                    <span className="text-xs text-slate-500">
                      Titular: <strong className="text-slate-700">{d.t_fc_nombre_asegurado}</strong>
                    </span>
                  </div>
                </td>

                {/* Columna 2: Siniestro */}
                <td className="p-4 px-6 align-top">
                  <div className="flex flex-col space-y-1.5">
                    <div className="font-mono text-sm font-bold text-brand-600">
                      {d.t_id} / {d.t_fc_siniestro?.trim()}
                    </div>
                    <div className="text-xs text-slate-500">
                      <strong className="text-slate-700">UA:</strong> {d.t_fi_agente_id} - {d.asig_fc_nombre} {d.asig_fc_apellido_paterno}
                    </div>
                    <div className="text-xs text-slate-500">
                      <strong className="text-slate-700">FR:</strong> {fecha_actual(d.t_fd_registro_tramite)}
                    </div>
                    <div className="text-xs text-slate-500">
                      <strong className="text-slate-700">FS:</strong> {d.t_fd_fecha_siniestro?.replace("T06:00:00.000Z", "")}
                    </div>
                  </div>
                </td>

                {/* Columna 3: Etapa */}
                <td className="p-4 px-6 align-top">
                  <div className="flex flex-col space-y-1.5">
                    <div className="inline-flex w-fit items-center rounded-md bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                      EA: {d.t_fc_etapa}
                    </div>
                    <div className="text-xs text-slate-500">
                      <strong className="text-slate-700">SE:</strong> {d.fc_sub_etapa}
                    </div>
                    <div className="text-xs text-slate-500">
                      <strong className="text-slate-700">FE:</strong> {fecha_actual(d.sem_fd_fecha_inicial)}
                    </div>
                    <div className="mt-1 border-t border-slate-100 pt-1 text-[11px] text-slate-400">
                      <strong className="text-slate-500">UM:</strong> {d.sem_fi_usuario} - {d.eje_fc_nombre} {d.eje_fc_apellido_paterno}
                    </div>
                  </div>
                </td>

                {/* Columna 4: Tiempos */}
                <td className="p-4 px-6 align-top">
                  <div className="flex flex-col space-y-2">
                    <div className="rounded-lg bg-slate-50 p-2 text-xs text-slate-600">
                      <span className="block font-bold text-slate-800">TR (Global)</span>
                      <span className="font-mono text-[11px]">{elapsed(d.t_fd_registro_tramite)}</span>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-2 text-xs text-slate-600">
                      <span className="block font-bold text-slate-800">TUE (Etapa)</span>
                      <span className="font-mono text-[11px]">{elapsed(d.sem_fd_fecha_inicial)}</span>
                    </div>
                  </div>
                </td>

                {/* Columna 5: Acción */}
                <td className="p-4 px-6 align-top text-right">
                  <div className="flex flex-col items-end space-y-2">
                    {texto_accion === "Asignar" ? (
                      <div className="flex w-48 flex-col gap-2">
                        <select
                          className="w-full cursor-pointer rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-800 transition-all hover:bg-white focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
                          name="ejecutivo_id"
                          onChange={(e) => setEje(e.target.value)}
                          value={eje}
                        >
                          <option value="">Seleccione Ejecutivo</option>
                          {ejecutivos?.map((b: any) => (
                            <option key={b.id} value={b.sub}>
                              {b.fc_nombre} {b.fc_apellido_paterno}
                            </option>
                          ))}
                        </select>

                        <button
                          disabled={cargando || !eje}
                          onClick={() => accion(d, eje)}
                          className="inline-flex w-full justify-center rounded-xl bg-brand-500 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:bg-brand-600 disabled:opacity-50"
                        >
                          {cargando ? <Spinner /> : texto_accion}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => accion(d)}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-slate-800"
                      >
                        {texto_accion}
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7"></path></svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
